<template>
  <div class="user">
    <Header
      :right-sidebar="true"
      right-sidebar-title="Редактирование пользователя"
    >
      <template #right-sidebar>
        <sidebar-items-group title="Отображать столбцы">
          <fields-picker
            v-model="pickedFields"
            :fields="displayableUserHistoryFields"
          />
        </sidebar-items-group>
        <sidebar-items-group title="Фильтры">
          <filters-group
            :fields="userHistoryFields"
            :items="userHistory"
            v-model="filteredItems"
          />
        </sidebar-items-group>
        <sidebar-item
          button
          variant="warning"
          @click="deleteUser"
          :disabled="loading"
          v-if="!isDeleted"
        >
          Удалить пользователя
          <template #icon>
            <b-icon icon="x-square" />
          </template>
        </sidebar-item>
        <sidebar-item
          button
          variant="success"
          @click="restoreUser"
          :disabled="loading"
          v-else
        >
          Восстановить пользователя
          <template #icon>
            <b-icon icon="box-arrow-up-right" />
          </template>
        </sidebar-item>
        <sidebar-item
          button
          variant="danger"
          @click="showPurgeConformalModal = true"
          :disabled="loading"
        >
          Удалить пользователя навсегда
          <template #icon>
            <b-icon icon="trash" />
          </template>
        </sidebar-item>
      </template>
    </Header>
    <Main>
      <h3 class="text-center">Редактирование пользователя</h3>
      <b-overlay :show="loading">
        <user-form
          v-model="userActualData"
          v-if="userActualData !== undefined"
          class="user__form"
        />
      </b-overlay>
    </Main>
    <Main>
      <b-overlay :show="loading">
        <h3 class="text-center">История редактирования пользователя</h3>
        <b-table
          selectable
          select-mode="single"
          :responsive="true"
          :items="paintedRows"
          :fields="pickedFields"
          hover
          class="table-rounded"
          empty-text="Записей нет"
        />
      </b-overlay>
    </Main>
    <b-modal
      centered
      title="Окончательное удаление пользователя"
      v-model="showPurgeConformalModal"
      ok-title="Удалить"
      cancel-title="Отмена"
      ok-variant="danger"
      cancel-variant="success"
      @ok="purgeUser"
    >
      <p class="my-4">
        Вы точно хотите удалить этого пользователя?<br />(История изменений и
        все связанные с этим пользователем данные будут безвозвратно утеряны.
        Данные пользователя больше не будут фигурировать в историях изменений
        других записей)
      </p>
    </b-modal>
    <bottom-overlay v-if="edited">
      <b-button
        @click="updateUser"
        block
        squared
        variant="success"
        class="user__update-button"
        :disabled="loading"
      >
        Сохранить изменения
      </b-button>
      <b-button
        @click="discardChanges"
        block
        squared
        variant="warning"
        class="user__discard-changes-button"
        :disabled="loading"
      >
        Отменить изменения
      </b-button>
    </bottom-overlay>
  </div>
</template>
<script>
import Main from "@/components/Layout/Main";
import Header from "@/components/Layout/Header";
import SidebarItemsGroup from "@/components/Layout/Sidebars/SidebarItems/SidebarItemsGroup";
import FieldsPicker from "@/components/Tables/FieldsPicker";
import FiltersGroup from "@/components/Tables/Filters/FiltersGroup";
import SidebarItem from "@/components/Layout/Sidebars/SidebarItems/SidebarItem";
import UserForm from "@/components/Forms/User/UserForm";
import UserModel from "@/components/Forms/User/models/userModel";
import BottomOverlay from "@/components/BottomOverlay";
import { displayableFields } from "@/components/Tables/defaultFields";
import { userHistoryFields } from "@/views/Users/models/usersTable";
import { defaultRowPainter } from "@/components/Tables/rowPainters";
import pageTitleMixin from "@/mixins/pageTitleMixin";

export default {
  components: {
    BottomOverlay,
    UserForm,
    SidebarItem,
    FiltersGroup,
    FieldsPicker,
    SidebarItemsGroup,
    Header,
    Main
  },
  mixins: [pageTitleMixin],
  data() {
    let displayableUserHistoryFields = displayableFields(userHistoryFields);
    return {
      userHistoryFields,
      displayableUserHistoryFields,
      filteredItems: [],
      pickedFields: displayableUserHistoryFields,
      userHistory: [],
      userActualData: undefined,
      loading: true,
      showPurgeConformalModal: false
    };
  },
  computed: {
    isDeleted: ({ userHistory }) =>
      userHistory[0] !== undefined
        ? Boolean(Number(userHistory[0].is_deleted))
        : false,
    edited: ({ userActualData, userHistory }) =>
      userHistory[0] !== undefined &&
      JSON.stringify(userActualData) !==
        JSON.stringify(new UserModel(userHistory[0])),
    paintedRows: ({ filteredItems }) => defaultRowPainter(filteredItems)
  },
  methods: {
    updateUser() {
      let data = { ...this.userActualData };
      if (data.hash === null) delete data.hash;
      this.loading = true;
      this.$store
        .dispatch("users/updateUser", {
          id: this.$route.params.id,
          userData: data
        })
        .then(async () => {
          if (
            Number(this.$store.getters["user/id"]) ===
              Number(this.$route.params.id) &&
            data.hash !== undefined &&
            data.hash !== null
          )
            await this.$store.dispatch("user/updateHash", data.hash);
          await this.loadData();
        })
        .catch(() => (this.loading = false));
    },
    discardChanges() {
      this.userActualData = new UserModel(this.userHistory[0]);
    },
    async deleteUser() {
      this.loading = true;
      await this.$store.dispatch("users/deleteUser", this.$route.params.id);
      await this.loadData();
    },
    async restoreUser() {
      this.loading = true;
      await this.$store.dispatch("users/restoreUser", this.$route.params.id);
      await this.loadData();
    },
    async purgeUser() {
      this.loading = true;
      await this.$store.dispatch("users/purgeUser", this.$route.params.id);
      await this.$router.push("/users");
    },
    async loadData() {
      await this.axios
        .get(`/users/${this.$route.params.id}/history`)
        .then(({ data }) => {
          this.userHistory = data.user_history;
          this.userActualData = new UserModel(this.userHistory[0]);
          this.loading = false;
        });
    }
  },
  async created() {
    await this.loadData();
  }
};
</script>

<style lang="scss">
.user {
  &__form {
    margin: 0 5px;
  }
}
</style>
